import React, { createContext, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useAlert } from '../../alert/containers/AlertProvider'
import {
  getChallengeParticipationListApi,
  acceptChallengeParticipationApi,
  cancelChallengeParticipationApi
} from '../api'
import 'moment/locale/fr'
import { useTranslation } from 'react-i18next'

moment.locale('fr')

export const ChallengeParticipationsContext = createContext()

export const initialFilter = {
  id: 0,
  orderBy: 0,
  date: {
    startDate: null,
    endDate: null,
  },
  statuses: [],
  wilayas: [],
  dairaId:[],
  communes: [],
  startTime: '00:00',
  endTime: '23:59',
  ownerPhoneNumber: '',
}

const initialPagination = { currentPage: 0, take: 10, skip: 0, total: 0 }

export const ChallengeParticipationsProvider = ({ children }) => {

  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const [challengeParticipationList, setChallengeParticipationList] = useState([])

  const [pagination, setPagination] = useState(initialPagination)
  const [filter, setFilter] = useState(initialFilter)

  const handlePagination = ({
    take = pagination.take,
    skip = pagination.skip,
    currentPage = pagination.currentPage,
  }) => {
    setPagination({ ...pagination, take, skip, currentPage })
  }

  const handleFilter = (newFilter) => {
    handlePagination({ currentPage: 0, skip: 0 })
    setFilter(newFilter)
  }

  const getChallengeParticipationsList = async () => {
    try {
      const {
        wilayas,
        dairaId,
        communes,
        date,
        ownerPhoneNumber,
        statuses,
        orderBy,
        id,
        startTime,
        endTime,
      } = filter
      const { take, skip } = pagination
      setIsLoading(true)

      const requestFilter = {
        ...(id.length > 0 && { id }),
        communeIds: communes.map(({ id }) => id),
        dairaIds: dairaId.map(({ id }) => id),
        wilayaIds: communes.length === 0 ? wilayas.map(({ id }) => id) : [],
        orderBy,
        statuses,
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} ${startTime}`,
          ).format(),
        }),
        // add 59 second to end time
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} ${endTime}:59`,
          ).format(),
        }),
        ...(ownerPhoneNumber.length > 3 && { ownerPhoneNumber }),
        take,
        skip,
      }

      const { data, headers } = await getChallengeParticipationListApi({
        filter: requestFilter,
      })

      let data2 = await data.map( element => { return {...element, ownerPhoneNumber: element.ownerPhoneNumber.replace('+213','0')} } )

      // here edit, remove later
      // let sortedArrayByLO = await data2.sort((element1, element2) => (new Date(element2.lastOperationDate)) - (new Date(element1.lastOperationDate)) )

      setChallengeParticipationList(data2)

      setPagination({ ...pagination, total: headers['x-total-count'] })
    } 
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const init = () => {
    setFilter({ ...initialFilter })
    setPagination({ ...initialPagination })
  }

  const acceptChallengeParticipation = async(challengeParticipationId) => {
    try {
      setIsLoading(true)

      await acceptChallengeParticipationApi(challengeParticipationId)
      acceptedChallengeParticipationStatus(challengeParticipationId)
      showAlert({
        variant: 'success',
        message: t('challengeParticipationsTable.participation_has_been_accepted')
      })
    } catch (e) {
       if ( e?.response?.data?.errorCode === "UserNotChallengeSupervisor") {
        showAlert({ message: t('showAlert.vous_netes_pas_autorise_a_effectuer_cette_action') })
      } else {
        showAlert({ message: t('showAlert.une_erreur_est_survenue')})
      }
    } finally {
      setIsLoading(false)
    }
  }

  const cancelChallengeParticipation = async(challengeParticipationId) => {
    try {
      setIsLoading(true)

      await cancelChallengeParticipationApi(challengeParticipationId)
      canceledChallengeParticipationStatus(challengeParticipationId)
      showAlert({
        variant: 'success',
        message: t('challengeParticipationsTable.participation_has_been_canceled')
      })
    } catch (e) {
       if ( e?.response?.data?.errorCode === "UserNotChallengeSupervisor") {
        showAlert({ message: t('showAlert.vous_netes_pas_autorise_a_effectuer_cette_action') })
      } else {
        showAlert({ message: t('showAlert.une_erreur_est_survenue')})
      }
    } finally {
      setIsLoading(false)
    }
  }

  const acceptedChallengeParticipationStatus = async ( id ) => {
    try {
      setChallengeParticipationList(
        challengeParticipationList.map((challengeParticipation) => {
          if (challengeParticipation.id === id) {
            return { ...challengeParticipation, status: 2 }
          }
          return challengeParticipation
        }),
      )
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    }
  }

  const canceledChallengeParticipationStatus = async ( id ) => {
    try {
      setChallengeParticipationList(
        challengeParticipationList.map((challengeParticipation) => {
          if (challengeParticipation.id === id) {
            return { ...challengeParticipation, status: 3 }
          }
          return challengeParticipation
        }),
      )
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    }
  }

  const providerValue = {
    init,
    challengeParticipationList,
    filter,
    statuses: [
      { id: 1, label: t('complaintTab.nouvelle') },
      { id: 2, label: t('challengeParticipationsTable.accepted') },
      { id: 3, label: t('complaintTab.annule') }
    ],
    pagination,
    isLoading,
    handleFilter,
    handlePagination,
    getChallengeParticipationsList,
    acceptChallengeParticipation,
    cancelChallengeParticipation
  }

  useEffect(() => {
    getChallengeParticipationsList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.take, pagination.skip])

  return (
    <ChallengeParticipationsContext.Provider value={providerValue}>
      {children}
    </ChallengeParticipationsContext.Provider>
  )
}
ChallengeParticipationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useChallengeParticipations = () => {
  const context = useContext(ChallengeParticipationsContext)
  if (context === undefined) {
    throw new Error('useChallengeParticipations must be used within a ChallengeParticipationsProvider')
  }
  return context
}